import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { usePortalStore } from '../../contexts/portalStore';
import { Loader2 } from 'lucide-react';

export function PortalPrivateRoute() {
  const location = useLocation();
  const { isAuthenticated, isTokenExpired, accessToken, _hasHydrated, expiresAt, account } = usePortalStore();

  // Debug logging
  console.log('[PortalPrivateRoute] Auth state:', {
    _hasHydrated,
    isAuthenticated,
    hasAccessToken: !!accessToken,
    expiresAt,
    isExpired: isTokenExpired(),
    needsPasswordSetup: account?.needsPasswordSetup,
    needsActivation: account?.needsActivation,
    currentPath: location.pathname,
  });

  // Wait for Zustand to rehydrate from localStorage before making auth decisions
  // This prevents false redirects on initial page load
  if (!_hasHydrated) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Not authenticated at all - redirect without "session expired" message
  if (!isAuthenticated || !accessToken) {
    return (
      <Navigate
        to="/portal"
        state={{ from: location }}
        replace
      />
    );
  }

  // Was authenticated but token expired - show session expired message
  if (isTokenExpired()) {
    return (
      <Navigate
        to="/portal?reason=session-expired"
        state={{ from: location }}
        replace
      />
    );
  }

  // SECURITY: Enforce password setup before allowing access to other protected routes
  // Users who haven't set their password can ONLY access /portal/set-password
  if (account?.needsPasswordSetup && location.pathname !== '/portal/set-password') {
    console.log('[PortalPrivateRoute] Redirecting to password setup - user has not set password');
    return (
      <Navigate
        to="/portal/set-password"
        replace
      />
    );
  }

  // After password is set, check if onboarding is needed
  // Users who haven't completed onboarding can access /portal/set-password or /portal/activation
  if (account?.needsActivation &&
      location.pathname !== '/portal/activation' &&
      location.pathname !== '/portal/set-password') {
    console.log('[PortalPrivateRoute] Redirecting to activation - user has not completed onboarding');
    return (
      <Navigate
        to="/portal/activation"
        replace
      />
    );
  }

  return <Outlet />;
}

export default PortalPrivateRoute;
